import { pluralizeWithCount } from "@common/helpers/string";
import { AccrualMatchDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import {
    AccrualMatchSuggestionDisplay,
    getDocumentSuggestions,
    getTransferSuggestion,
} from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";

/**
 * Structural type that both `AccrualMatchDisplay` (already-reviewed
 * matches) and `AccrualMatchSuggestionDisplay` (suggestions) are
 * assignable to, so the same aggregator can summarise either source.
 *
 * Reviewed matches expose the paired transfer under `pairedTransaction`;
 * suggestions expose it under `matchedTransaction`.
 */
export interface MatchSummaryItem {
    invoice?:
        | AccrualMatchDisplay["invoice"]
        | AccrualMatchSuggestionDisplay["invoice"];
    bill?: AccrualMatchDisplay["bill"] | AccrualMatchSuggestionDisplay["bill"];
    pairedTransaction?: AccrualMatchDisplay["pairedTransaction"];
    matchedTransaction?: AccrualMatchSuggestionDisplay["matchedTransaction"];
}

type MatchKind = "invoice" | "bill" | "transfer";

interface AggregatedMatchItem {
    name: string | null;
    amount: number;
    kind: MatchKind;
}

interface AggregatedMatches {
    /**
     * Number of input items that successfully resolved into an
     * invoice/bill/transfer entry. Use this instead of inspecting
     * `countLabel` when callers need to detect the "nothing renderable"
     * case — `countLabel` is presentation-only and can change wording.
     */
    resolvedCount: number;
    firstName: string | null;
    extraNamesCount: number;
    totalAmount: number;
    countLabel: string;
}

function resolveItem(item: MatchSummaryItem): AggregatedMatchItem | null {
    if (item.invoice) {
        return {
            name: item.invoice.counterpartyName ?? null,
            amount: item.invoice.total,
            kind: "invoice",
        };
    }
    if (item.bill) {
        return {
            name: item.bill.counterpartyName ?? null,
            amount: item.bill.total,
            kind: "bill",
        };
    }
    if (item.matchedTransaction) {
        return {
            name: item.matchedTransaction.displayDescription ?? null,
            amount: item.matchedTransaction.amount,
            kind: "transfer",
        };
    }
    if (item.pairedTransaction) {
        return {
            name: item.pairedTransaction.displayDescription ?? null,
            amount: item.pairedTransaction.amount,
            kind: "transfer",
        };
    }
    return null;
}

export function getSuggestedMatchPreviewItems(
    suggestions: readonly AccrualMatchSuggestionDisplay[] | null | undefined,
): AccrualMatchSuggestionDisplay[] {
    const suggestionList = [...(suggestions ?? [])];
    const documentSuggestions = getDocumentSuggestions(suggestionList);
    if (documentSuggestions.length > 0) {
        return documentSuggestions;
    }
    const transferSuggestion = getTransferSuggestion(suggestionList);
    if (transferSuggestion?.matchedTransaction) {
        return [transferSuggestion];
    }
    return [];
}

export function aggregateMatches(
    items: readonly MatchSummaryItem[],
): AggregatedMatches {
    const resolved = items
        .map(resolveItem)
        .filter((entry): entry is AggregatedMatchItem => entry !== null);

    const totalAmount = resolved.reduce((sum, entry) => sum + entry.amount, 0);

    const namedEntries = resolved.filter(
        (entry): entry is AggregatedMatchItem & { name: string } =>
            !!entry.name,
    );
    const firstName = namedEntries[0]?.name ?? null;
    // Dedupe across the whole set first, then subtract the first name.
    // Slicing off the first entry (rather than the first name) would
    // double-count duplicates of `firstName` in the "+N" chip.
    const distinctNames = new Set(namedEntries.map((entry) => entry.name));
    const extraNamesCount = distinctNames.size > 0 ? distinctNames.size - 1 : 0;

    const kinds = new Set(resolved.map((entry) => entry.kind));
    const countLabel =
        kinds.size === 1 && resolved.length > 0
            ? pluralizeWithCount(resolved[0].kind, resolved.length)
            : `${resolved.length} matches`;

    return {
        resolvedCount: resolved.length,
        firstName,
        extraNamesCount,
        totalAmount,
        countLabel,
    };
}
